import { useState, useEffect } from 'react';
import {
  BarChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ComposedChart,
} from 'recharts';
import api from '../../api/client';

const C = {
  navy: '#1B2A4A', navyMuted: '#3A5080', burgundy: '#7A2E3B',
  gold: '#B8953E', hunter: '#2D5F4A', ink: '#1A1A1A',
  textMuted: '#7A7265', textLight: '#A69D91',
  ivory: '#FAF7F2', cream: '#F3EDE4', border: '#D6CCBE', borderLight: '#E8E0D4',
};

function fmt(v: number | null | undefined): string {
  if (v == null) return '—';
  const abs = Math.abs(v); const neg = v < 0;
  if (abs >= 1e6) return `${neg ? '(' : ''}$${(abs / 1e6).toFixed(1)}M${neg ? ')' : ''}`;
  if (abs >= 1000) return `${neg ? '(' : ''}$${(abs / 1000).toFixed(0)}K${neg ? ')' : ''}`;
  return `${neg ? '(' : ''}$${abs.toFixed(0)}${neg ? ')' : ''}`;
}

function StatCard({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div className="flex-1 min-w-[130px] px-5 py-4 border-r border-stone-200 last:border-r-0">
      <p className="text-[10px] font-sans font-bold text-stone-400 tracking-widest uppercase mb-2">{label}</p>
      <p className="text-2xl font-display font-semibold leading-none" style={{ color: accent || C.ink }}>{value}</p>
      {sub && <p className="text-[11px] font-sans text-stone-500 mt-1.5">{sub}</p>}
    </div>
  );
}

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`bg-ivory-50 border border-stone-300 rounded-sm ${className}`}>{children}</div>;
}

function SectionTitle({ children, sub }: { children: React.ReactNode; sub?: string }) {
  return (
    <div className="mb-4">
      <h3 className="text-[13px] font-sans font-bold text-stone-500 tracking-widest uppercase border-b-2 border-navy-800 inline-block pb-1">{children}</h3>
      {sub && <p className="text-[13px] font-body text-stone-400 italic mt-1.5">{sub}</p>}
    </div>
  );
}

function AlertBox({ type, title, children }: { type: 'danger' | 'warning' | 'success' | 'info'; title?: string; children: React.ReactNode }) {
  const styles: Record<string, { bg: string; border: string; tc: string }> = {
    danger: { bg: 'bg-red-50', border: 'border-l-red-800', tc: 'text-red-800' },
    warning: { bg: 'bg-amber-50', border: 'border-l-amber-600', tc: 'text-amber-700' },
    success: { bg: 'bg-emerald-50', border: 'border-l-emerald-700', tc: 'text-emerald-700' },
    info: { bg: 'bg-blue-50', border: 'border-l-blue-800', tc: 'text-blue-800' },
  };
  const s = styles[type];
  return (
    <div className={`${s.bg} ${s.border} border-l-[3px] rounded-sm p-3.5 mb-2.5`}>
      {title && <p className={`font-sans text-[13px] font-bold ${s.tc} mb-1`}>{title}</p>}
      <div className="font-body text-[12px] text-stone-700 leading-relaxed">{children}</div>
    </div>
  );
}

function StatusPill({ status, label }: { status: string; label: string }) {
  const map: Record<string, string> = {
    current: 'bg-emerald-100 text-emerald-800', danger: 'bg-red-100 text-red-800',
    warning: 'bg-amber-100 text-amber-800', info: 'bg-blue-100 text-blue-800',
  };
  return <span className={`inline-block px-2.5 py-0.5 rounded-sm text-[10px] font-sans font-bold tracking-wide uppercase ${map[status] || map.info}`}>{label}</span>;
}

const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload) return null;
  return (
    <div className="bg-ivory-50 border border-stone-300 rounded-sm px-3.5 py-2.5 shadow-lg">
      <p className="text-[11px] font-sans font-semibold text-stone-400 mb-1">{label}</p>
      {payload.map((e: any, i: number) => (
        <p key={i} className="text-[12px] font-sans font-medium" style={{ color: e.color || C.ink }}>
          {e.name}: {fmt(e.value)}
        </p>
      ))}
    </div>
  );
};

export default function TimingOverlayPage() {
  const [scenario, setScenario] = useState<'current' | 'lower'>('current');
  const [horizonYears, setHorizonYears] = useState(6);
  const [data, setData] = useState<any>(null);
  const [compareData, setCompareData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showCompare, setShowCompare] = useState(false);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [simRes, compareRes] = await Promise.all([
          api.get(`/timing/simulate?sunset=${scenario === 'lower'}&horizon=${horizonYears}`),
          api.get(`/timing/compare?horizon=${horizonYears}`),
        ]);
        setData(simRes.data.data);
        setCompareData(compareRes.data.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    }
    loadData();
  }, [scenario, horizonYears]);

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-[14px] font-sans text-stone-400 animate-pulse">Running timing simulations...</p></div>;
  if (!data) return <div className="p-6"><AlertBox type="warning" title="No Data">Add assets and liabilities to run timing simulations.</AlertBox></div>;

  const { yearlyResults, summary } = data;

  return (
    <div>
      {/* KPI Bar */}
      <Card className="flex mb-6 overflow-hidden flex-wrap">
        <StatCard label="Tax Range" value={`${fmt(summary.taxRange.min)} – ${fmt(summary.taxRange.max)}`} sub={`Over ${horizonYears} years`} accent={C.burgundy} />
        <StatCard label="Worst Year" value={`Year ${summary.worstYear.year}`} sub={`Gap: ${fmt(summary.worstYear.gap)}`} accent={C.burgundy} />
        <StatCard label="Best Year" value={`Year ${summary.bestYear.year}`} sub={`Gap: ${fmt(summary.bestYear.gap)}`} accent={C.hunter} />
        <StatCard label="Strategy Savings" value={fmt(summary.totalStrategySavingsAtFull)} sub="At full implementation" accent={C.hunter} />
        <StatCard label="§6166" value={summary.section6166LostAtYear ? `Lost Yr ${summary.section6166LostAtYear}` : 'Eligible'} accent={summary.section6166LostAtYear ? C.gold : C.hunter} />
      </Card>

      {/* Controls */}
      <div className="flex items-center gap-4 mb-5 flex-wrap">
        {/* Scenario Toggle */}
        <div className="flex gap-2">
          {[
            { id: 'current' as const, label: 'Current Law ($15M)' },
            { id: 'lower' as const, label: 'Reduced Exemption (~$7M)' },
          ].map(s => (
            <button key={s.id} onClick={() => setScenario(s.id)}
              className={`px-4 py-2 rounded-sm text-[12px] font-sans font-semibold border transition-all ${
                scenario === s.id ? 'border-navy-800 text-ivory-100' : 'border-stone-300 text-stone-500 bg-ivory-50'
              }`}
              style={scenario === s.id ? { background: C.navy } : {}}>
              {s.label}
            </button>
          ))}
        </div>

        {/* Horizon Selector */}
        <div className="flex items-center gap-2 ml-2">
          <span className="text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase">Horizon</span>
          <div className="flex gap-1 bg-stone-200 rounded-sm p-0.5">
            {[3, 6, 10, 15, 20].map(yr => (
              <button key={yr} onClick={() => setHorizonYears(yr)}
                className={`px-3 py-1.5 rounded-sm text-[11px] font-sans font-semibold transition-all ${
                  horizonYears === yr ? 'text-ivory-100' : 'text-stone-500 bg-transparent'
                }`}
                style={horizonYears === yr ? { background: C.navy, color: C.ivory } : {}}>
                {yr}yr
              </button>
            ))}
          </div>
        </div>

        {/* Compare Toggle */}
        <button onClick={() => setShowCompare(!showCompare)}
          className={`ml-auto px-4 py-2 rounded-sm text-[12px] font-sans font-semibold border transition-all ${
            showCompare ? 'border-amber-600 text-ivory-100' : 'border-stone-300 text-stone-500 bg-ivory-50'
          }`}
          style={showCompare ? { background: C.gold } : {}}>
          {showCompare ? 'Hide Comparison' : 'Compare Both'}
        </button>
      </div>

      {/* Combined Tax vs Liquidity Chart */}
      <Card className="p-6 mb-5">
        <SectionTitle sub={`Estate tax due versus available liquidity at each modeled death year (${horizonYears}-year horizon)`}>
          Death-at-Year-N Simulation
        </SectionTitle>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={yearlyResults.map((yr: any) => ({
            label: `Year ${yr.yearNum}`,
            federalTax: yr.federalTax,
            stateTax: yr.stateTax,
            liquidity: yr.totalLiquidity,
            gap: yr.liquidityGap,
          }))}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.borderLight} />
            <XAxis dataKey="label" tick={{ fill: C.textLight, fontSize: horizonYears > 10 ? 9 : 12 }} axisLine={{ stroke: C.border }} />
            <YAxis tick={{ fill: C.textLight, fontSize: 11 }} axisLine={{ stroke: C.border }} tickFormatter={(v: number) => fmt(v)} />
            <Tooltip content={<ChartTooltip />} />
            <Bar dataKey="federalTax" stackId="tax" fill={C.burgundy} name="Federal Tax" barSize={horizonYears > 10 ? 16 : 34} fillOpacity={0.8} />
            <Bar dataKey="stateTax" stackId="tax" fill={C.navyMuted} name="State Tax" barSize={horizonYears > 10 ? 16 : 34} radius={[3, 3, 0, 0]} fillOpacity={0.8} />
            <Bar dataKey="liquidity" fill={C.hunter} name="Available Liquidity" barSize={horizonYears > 10 ? 16 : 34} radius={[3, 3, 0, 0]} fillOpacity={0.7} />
            <Line type="monotone" dataKey="gap" stroke={C.gold} strokeWidth={2.5} dot={{ fill: C.gold, r: horizonYears > 10 ? 3 : 5, strokeWidth: 0 }} name="Liquidity Gap" />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>

      {/* Comparison Chart */}
      {showCompare && compareData && (
        <Card className="p-6 mb-5">
          <SectionTitle sub="Current law vs reduced exemption — combined estate tax at each death year">
            Exemption Scenario Comparison
          </SectionTitle>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={compareData.currentLaw.yearlyResults.map((yr: any, i: number) => ({
              label: `Year ${yr.yearNum}`,
              currentLaw: yr.combinedTax,
              reducedExemption: compareData.sunset.yearlyResults[i]?.combinedTax || 0,
            }))} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.borderLight} />
              <XAxis dataKey="label" tick={{ fill: C.textLight, fontSize: horizonYears > 10 ? 9 : 12 }} axisLine={{ stroke: C.border }} />
              <YAxis tick={{ fill: C.textLight, fontSize: 11 }} axisLine={{ stroke: C.border }} tickFormatter={(v: number) => fmt(v)} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="currentLaw" fill={C.navy} name="Current Law" barSize={horizonYears > 10 ? 14 : 28} radius={[3, 3, 0, 0]} fillOpacity={0.8} />
              <Bar dataKey="reducedExemption" fill={C.burgundy} name="Reduced Exemption" barSize={horizonYears > 10 ? 14 : 28} radius={[3, 3, 0, 0]} fillOpacity={0.8} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Detail Table + Insights */}
      <div className="grid grid-cols-3 gap-5">
        <div className="col-span-2">
          <Card className="overflow-hidden">
            <div className="px-6 pt-5 pb-3"><SectionTitle>Year-by-Year Detail</SectionTitle></div>
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-ivory-200 sticky top-0">
                    {['Year', 'Gross Estate', 'Debt', 'Fed Tax', 'State Tax', 'Combined', 'Liquidity', 'Gap', '§6166'].map(h => (
                      <th key={h} className="text-[10px] font-sans font-bold text-stone-400 tracking-widest uppercase text-left px-3 py-2.5 border-b-2 border-stone-300 bg-ivory-200">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {yearlyResults.map((yr: any, i: number) => (
                    <tr key={yr.yearNum} className={i % 2 === 0 ? '' : 'bg-ivory-200'}>
                      <td className="px-3 py-2.5 text-[12px] font-sans font-semibold text-stone-800 border-b border-stone-200">Yr {yr.yearNum}</td>
                      <td className="px-3 py-2.5 text-[11px] font-mono text-stone-600 border-b border-stone-200">{fmt(yr.grossEstate)}</td>
                      <td className="px-3 py-2.5 text-[11px] font-mono text-stone-600 border-b border-stone-200">{fmt(yr.debtDeduction)}</td>
                      <td className="px-3 py-2.5 text-[11px] font-mono border-b border-stone-200" style={{ color: C.burgundy }}>{fmt(yr.federalTax)}</td>
                      <td className="px-3 py-2.5 text-[11px] font-mono border-b border-stone-200" style={{ color: C.navyMuted }}>{fmt(yr.stateTax)}</td>
                      <td className="px-3 py-2.5 text-[11px] font-mono font-semibold border-b border-stone-200" style={{ color: C.ink }}>{fmt(yr.combinedTax)}</td>
                      <td className="px-3 py-2.5 text-[11px] font-mono border-b border-stone-200" style={{ color: C.hunter }}>{fmt(yr.totalLiquidity)}</td>
                      <td className="px-3 py-2.5 text-[11px] font-mono border-b border-stone-200" style={{ color: yr.liquidityGap > 0 ? C.gold : C.hunter }}>{yr.liquidityGap > 0 ? fmt(yr.liquidityGap) : '—'}</td>
                      <td className="px-3 py-2.5 border-b border-stone-200">
                        <StatusPill status={yr.section6166Eligible ? 'current' : 'danger'} label={yr.section6166Eligible ? '✓' : '✗'} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        <Card className="p-6">
          <SectionTitle>Timing Insights</SectionTitle>
          {summary.worstYear.gap > 0 && (
            <AlertBox type="danger" title={`Year ${summary.worstYear.year} — Worst Case`}>
              Combined tax of {fmt(summary.worstYear.tax)} with a liquidity gap of {fmt(summary.worstYear.gap)}.
              {summary.section6166LostAtYear ? ` §6166 deferral lost at Year ${summary.section6166LostAtYear}.` : ''}
            </AlertBox>
          )}
          {summary.bestYear.gap === 0 && (
            <AlertBox type="success" title={`Year ${summary.bestYear.year} — Best Case`}>
              Liquidity fully covers the {fmt(summary.bestYear.tax)} tax obligation. No gap.
            </AlertBox>
          )}
          {summary.section6166LostAtYear && (
            <AlertBox type="warning" title="§6166 Paradox">
              Debt payoff eliminates §6166 eligibility at Year {summary.section6166LostAtYear}. The 14-year deferral option is lost when it's needed most.
            </AlertBox>
          )}
          <AlertBox type="info" title="Strategy Phasing">
            Strategies phase in over the horizon. At full implementation, savings reach {fmt(summary.totalStrategySavingsAtFull)}.
            {summary.ilitProceeds > 0 ? ` ILIT provides ${fmt(summary.ilitProceeds)} outside the estate.` : ''}
          </AlertBox>
          {scenario === 'current' && (
            <AlertBox type="info" title="Scenario Modeling">
              Using {fmt(data.exemption)} exemption (OBBBA permanent). Toggle "Reduced Exemption" to model a hypothetical lower exemption.
            </AlertBox>
          )}
          {horizonYears > 10 && (
            <AlertBox type="info" title="Extended Horizon">
              Modeling {horizonYears} years. Longer horizons show more strategy impact but carry greater assumption risk. Validate growth and inflation assumptions.
            </AlertBox>
          )}
        </Card>
      </div>
    </div>
  );
}
